import * as React from "react"
import { ChevronDown, Check } from "lucide-react"
import { cn } from "../../lib/utils"

export interface SelectProps {
    value: string
    onChange: (value: string) => void
    options: Array<{ value: string; label: string }>
    placeholder?: string
    className?: string
    disabled?: boolean
}

const Select = React.forwardRef<HTMLDivElement, SelectProps>(
    ({ value, onChange, options, placeholder = "Select option", className, disabled = false }, ref) => {
        const [isOpen, setIsOpen] = React.useState(false)
        const [selectedOption, setSelectedOption] = React.useState(() =>
            options.find(opt => opt.value === value) || null
        )
        const selectRef = React.useRef<HTMLDivElement>(null)

        React.useEffect(() => {
            setSelectedOption(options.find(opt => opt.value === value) || null)
        }, [value, options])

        React.useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                    setIsOpen(false)
                }
            }

            document.addEventListener('mousedown', handleClickOutside)
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }, [])

        const handleSelect = (option: { value: string; label: string }) => {
            setSelectedOption(option)
            onChange(option.value)
            setIsOpen(false)
        }

        const toggleDropdown = () => {
            if (!disabled) {
                setIsOpen(!isOpen)
            }
        }

        return (
            <div className={ cn("relative", className) } ref={ ref || selectRef }>
                <button
                    type="button"
                    onClick={ toggleDropdown }
                    disabled={ disabled }
                    className={ cn(
                        "flex h-10 w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-card-foreground",
                        isOpen ? "ring-2 ring-primary ring-offset-2" : "",
                        className
                    ) }
                >
                    <span className={ selectedOption ? "text-card-foreground" : "text-muted-foreground" }>
                        { selectedOption ? selectedOption.label : placeholder }
                    </span>
                    <ChevronDown
                        className={ cn(
                            "h-4 w-4 text-muted-foreground transition-transform duration-200",
                            isOpen ? "rotate-180" : ""
                        ) }
                    />
                </button>

                { isOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-card shadow-lg">
                        <div className="max-h-60 overflow-auto p-1">
                            { options.map((option) => (
                                <button
                                    key={ option.value }
                                    type="button"
                                    onClick={ () => handleSelect(option) }
                                    className={ cn(
                                        "flex w-full items-center justify-between rounded px-3 py-2 text-sm hover:bg-accent focus:bg-accent focus:outline-none",
                                        option.value === value ? "bg-accent text-accent-foreground" : "text-card-foreground"
                                    ) }
                                >
                                    <span>{ option.label }</span>
                                    { option.value === value && (
                                        <Check className="h-4 w-4 text-accent-foreground" />
                                    ) }
                                </button>
                            )) }
                        </div>
                    </div>
                ) }
            </div>
        )
    }
)
Select.displayName = "Select"

export { Select } 